import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import DashboardQuickActions from '../../components/navigation/DashboardQuickActions';
import TrafficMap from './components/TrafficMap';
import RoadConditionsCard from './components/RoadConditionsCard';
import AccountStatusCard from './components/AccountStatusCard';
import { getSession } from '../../utils/auth';
import { AccountStatusSkeleton, RoadConditionsSkeleton } from '../../components/SkeletonLoader';
import {
  HexagonPatternDash,
  DiagonalStripesDash,
  CrosshairMarkDash,
  TriangleAccentDash,
  HandDrawnUnderlineDash,
  GridDotsDash,
  AngularAccentDash,
  HandDrawnConnectorDash,
  useScrollReveal,
} from './components/DashboardGeometricPatterns';

// Empty state illustration for dashboard
const DashboardEmptyState = () => (
  <div
    className="flex flex-col items-center justify-center text-center"
    style={{
      background: 'var(--color-card)',
      border: '2px solid var(--color-border)',
      borderRadius: 'var(--radius-md)',
      padding: '64px 32px',
      minHeight: '320px',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* Custom SVG illustration */}
    <svg width="120" height="100" viewBox="0 0 120 100" fill="none" style={{ marginBottom: '24px', opacity: 0.85 }}>
      {/* Road */}
      <rect x="40" y="60" width="40" height="30" rx="2" fill="rgba(0,79,152,0.12)" stroke="rgba(0,79,152,0.3)" strokeWidth="1.5"/>
      <line x1="60" y1="65" x2="60" y2="85" stroke="rgba(0,79,152,0.4)" strokeWidth="2" strokeDasharray="4 3"/>
      {/* Car body */}
      <rect x="46" y="38" width="28" height="18" rx="3" fill="rgba(0,79,152,0.2)" stroke="rgba(0,79,152,0.5)" strokeWidth="1.5"/>
      <rect x="50" y="32" width="20" height="10" rx="2" fill="rgba(0,79,152,0.15)" stroke="rgba(0,79,152,0.4)" strokeWidth="1"/>
      {/* Wheels */}
      <circle cx="52" cy="57" r="4" fill="rgba(0,79,152,0.3)" stroke="rgba(0,79,152,0.6)" strokeWidth="1.5"/>
      <circle cx="68" cy="57" r="4" fill="rgba(0,79,152,0.3)" stroke="rgba(0,79,152,0.6)" strokeWidth="1.5"/>
      {/* Toll gate */}
      <line x1="20" y1="30" x2="20" y2="70" stroke="rgba(255,87,34,0.5)" strokeWidth="2"/>
      <line x1="20" y1="42" x2="50" y2="42" stroke="rgba(255,87,34,0.6)" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="20" cy="30" r="4" fill="rgba(255,87,34,0.3)" stroke="rgba(255,87,34,0.6)" strokeWidth="1.5"/>
      {/* Signal dots */}
      <circle cx="35" cy="20" r="3" fill="rgba(16,185,129,0.6)"/>
      <circle cx="45" cy="20" r="3" fill="rgba(245,158,11,0.4)"/>
      <circle cx="55" cy="20" r="3" fill="rgba(239,68,68,0.4)"/>
      {/* Geometric accents */}
      <line x1="85" y1="15" x2="110" y2="15" stroke="rgba(255,87,34,0.3)" strokeWidth="1.5"/>
      <line x1="85" y1="15" x2="85" y2="40" stroke="rgba(255,87,34,0.3)" strokeWidth="1.5"/>
      <circle cx="10" cy="80" r="6" fill="none" stroke="rgba(0,79,152,0.2)" strokeWidth="1.5"/>
      <circle cx="10" cy="80" r="2" fill="rgba(0,79,152,0.3)"/>
    </svg>
    <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '20px', color: 'var(--color-foreground)', marginBottom: '10px' }}>
      Ingen data tilgjengelig
    </h3>
    <p style={{ fontFamily: 'var(--font-caption)', fontSize: '14px', color: 'var(--color-muted-foreground)', maxWidth: '280px', lineHeight: 1.6 }}>
      Logg inn for å se kontostatus og veiforhold.
    </p>
  </div>
);

const Dashboard = () => {
  const session = getSession();
  const userName = session?.name ? session?.name?.split(' ')?.[0] : 'bruker';
  const isPremium = session?.accountTier === 'premium';
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const [headerRef, headerVisible] = useScrollReveal({ threshold: 0.1 });
  const [leftColRef, leftColVisible] = useScrollReveal({ threshold: 0.08 });
  const [rightColRef, rightColVisible] = useScrollReveal({ threshold: 0.06 });
  const [mobileActionsRef, mobileActionsVisible] = useScrollReveal({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-background" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Architectural background geometry */}
      <div style={{ position: 'fixed', top: '80px', right: '-120px', width: '500px', height: '500px', border: '2px solid rgba(0,79,152,0.05)', borderRadius: '2px', transform: 'rotate(12deg)', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'fixed', bottom: '100px', left: '-80px', width: '320px', height: '320px', border: '2px solid rgba(255,87,34,0.04)', borderRadius: '2px', transform: 'rotate(-8deg)', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'fixed', top: '40%', left: '45%', width: '160px', height: '160px', border: '1px solid rgba(0,79,152,0.04)', borderRadius: '2px', transform: 'rotate(25deg)', pointerEvents: 'none', zIndex: 0 }} />

      {/* Fixed SVG background patterns */}
      <HexagonPatternDash className="fixed" style={{ top: '120px', left: '-60px', opacity: 0.5, zIndex: 0 }} />
      <GridDotsDash className="fixed" rows={6} cols={10} style={{ top: '60%', right: '0', zIndex: 0 }} />

      <Header />

      <main className="pt-[84px] px-4 sm:px-6 lg:px-8 pb-8 max-w-[1440px] mx-auto" style={{ position: 'relative', zIndex: 1 }}>
        {/* Asymmetrical Page Header with SVG accents + scroll reveal */}
        <div
          ref={headerRef}
          className="mb-6 sm:mb-8 lg:mb-10"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'flex-end',
            gap: '16px',
            position: 'relative',
            overflow: 'visible',
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0px)' : 'translateY(28px)',
            transition: 'opacity 0.65s cubic-bezier(0.22,1,0.36,1), transform 0.65s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          {/* SVG geometric overlays on header */}
          <DiagonalStripesDash
            style={{ top: '-10px', right: '60px', width: '120px', height: '120px', opacity: 0.6 }}
          />
          <TriangleAccentDash
            style={{ top: '0px', right: '0px' }}
            size={48}
            color="#FF5722"
          />
          <CrosshairMarkDash
            style={{ bottom: '8px', right: '80px' }}
            size={18}
            color="#004F98"
          />
          <AngularAccentDash
            style={{ bottom: '-8px', left: '0px' }}
            width={50}
            height={40}
            color="#FF5722"
          />

          <div style={{ position: 'relative' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '28px', height: '2px', background: 'var(--color-accent)' }} />
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)' }}>
                Kontrollpanel
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-foreground font-heading mb-2">
              Oversikt
            </h1>
            {/* Hand-drawn underline under heading */}
            <HandDrawnUnderlineDash width={110} className="mb-2" />
            <p className="text-sm sm:text-base text-muted-foreground font-caption">
              Velkommen tilbake, <span style={{ color: 'var(--color-foreground)', fontWeight: 500 }}>{userName}</span>. Her er dagens veiinformasjon.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                border: `2px solid ${isPremium ? 'rgba(245,158,11,0.3)' : 'rgba(156,163,175,0.2)'}`,
                borderRadius: 'var(--radius-sm)',
                background: isPremium ? 'rgba(245,158,11,0.07)' : 'rgba(156,163,175,0.04)',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill={isPremium ? '#F59E0B' : 'none'} stroke={isPremium ? '#F59E0B' : 'var(--color-muted-foreground)'} strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: isPremium ? '#F59E0B' : 'var(--color-muted-foreground)' }}>
                {isPremium ? 'Premium' : 'Gratis'}
              </span>
            </div>
          </div>
        </div>

        {/* Asymmetrical Grid Layout — 5/7 split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column — scroll reveal with slight delay */}
          <div
            ref={leftColRef}
            className="lg:col-span-5 space-y-4 sm:space-y-6"
            style={{
              paddingTop: '0',
              opacity: leftColVisible ? 1 : 0,
              transform: leftColVisible ? 'translateX(0px)' : 'translateX(-24px)',
              transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s',
            }}
          >
            {isLoading ? (
              <>
                <AccountStatusSkeleton />
                <RoadConditionsSkeleton />
              </>
            ) : !session ? (
              <DashboardEmptyState />
            ) : (
              <>
                <AccountStatusCard />
                <RoadConditionsCard />
              </>
            )}
          </div>

          {/* Right Column — Map with overlapping Quick Actions */}
          <div
            ref={rightColRef}
            className="lg:col-span-7 relative"
            style={{
              opacity: rightColVisible ? 1 : 0,
              transform: rightColVisible ? 'translateX(0px)' : 'translateX(24px)',
              transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1) 0.18s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.18s',
            }}
          >
            {/* Offset accent bar */}
            <div style={{ position: 'absolute', top: '-8px', left: '0', right: '40px', height: '3px', background: 'linear-gradient(90deg, var(--color-primary) 0%, transparent 100%)', zIndex: 3 }} />
            {/* SVG accents on map column */}
            <HandDrawnConnectorDash style={{ top: '20px', right: '-2px', zIndex: 2 }} />
            <CrosshairMarkDash style={{ top: '8px', right: '20px', zIndex: 2 }} size={16} color="#FF5722" />
            <div className="h-[400px] sm:h-[500px] lg:h-[700px] relative">
              <TrafficMap />
              <div className="hidden lg:block">
                <DashboardQuickActions />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions — Mobile/Tablet */}
        <div
          ref={mobileActionsRef}
          className="lg:hidden mt-6 sm:mt-8"
          style={{
            opacity: mobileActionsVisible ? 1 : 0,
            transform: mobileActionsVisible ? 'translateY(0px)' : 'translateY(20px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.1s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.1s',
          }}
        >
          <div className="bg-card border-2 border-border rounded-[var(--radius-md)] p-4 sm:p-6" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '-10px', right: '-10px', width: '80px', height: '80px', border: '1px solid rgba(0,79,152,0.08)', borderRadius: '2px', transform: 'rotate(15deg)', pointerEvents: 'none' }} />
            {/* SVG accents on mobile quick actions */}
            <GridDotsDash rows={3} cols={5} style={{ position: 'absolute', bottom: '8px', right: '8px', opacity: 0.7 }} />
            <TriangleAccentDash style={{ position: 'absolute', bottom: '12px', left: '12px' }} size={32} color="#004F98" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{ width: '20px', height: '2px', background: 'var(--color-accent)' }} />
              <h3 className="text-lg font-semibold text-foreground font-heading">Hurtighandlinger</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => window.location.href = '/toll-calculator'}
                className="quick-action-button group"
                aria-label="Beregn bomkostnader"
                style={{ transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                onMouseDown={e => e.currentTarget.style.transform = 'scale(0.97)'}
                onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded flex items-center justify-center transition-all" style={{ background: 'rgba(0, 79, 152, 0.15)', transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" style={{ transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}>
                      <rect x="4" y="4" width="16" height="16" rx="2" />
                      <path d="M9 9h6M9 13h6M9 17h6" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-foreground mb-1 group-hover:text-primary transition-colors">Beregn bomkostnader</div>
                    <div className="text-sm text-muted-foreground font-caption">Planlegg rute og optimaliser kostnader</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => window.location.href = '/subscription-portal'}
                className="quick-action-button group"
                aria-label="Administrer abonnement"
                style={{ transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease-out' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                onMouseDown={e => e.currentTarget.style.transform = 'scale(0.97)'}
                onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded flex items-center justify-center transition-all" style={{ background: 'rgba(255, 87, 34, 0.15)', transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2" style={{ transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}>
                      <rect x="1" y="4" width="22" height="16" rx="2" />
                      <path d="M1 10h22" />
                    </svg>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-foreground mb-1 group-hover:text-primary transition-colors">Administrer abonnement</div>
                    <div className="text-sm text-muted-foreground font-caption">Håndter betalinger og kjøretøy</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;